import { useEffect, useState } from 'react';
import api from '../api';
import { X, FileText, ExternalLink, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';

/**
 * Admin modal: generates (or regenerates) a $2,500 draft Stripe invoice
 * against the factoring partner, linked to a signed ETA-671 agreement.
 *
 * - Amount is server-enforced from the agreement's stated_tuition.
 * - Partner name/email default from env on the backend but can be edited
 *   per invoice right here.
 * - After creation, shows the Stripe dashboard URL so the admin can jump
 *   straight to the draft to review / finalize / send.
 */
const FactoringInvoiceModal = ({ apiBaseUrl, agreement, onClose, onSaved }) => {
  const [partnerName, setPartnerName] = useState('');
  const [partnerEmail, setPartnerEmail] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(agreement.factoring_invoice || null);
  const [stripeMode, setStripeMode] = useState(null);
  const [stripeConfigured, setStripeConfigured] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/apprenticeship/factoring-config`);
        if (cancelled) return;
        setStripeMode(r.data?.stripe_mode || null);
        setStripeConfigured(!!r.data?.stripe_configured);
        // Prefill from existing invoice if present, else from defaults
        if (agreement.factoring_invoice) {
          setPartnerName(agreement.factoring_invoice.partner_name || '');
          setPartnerEmail(agreement.factoring_invoice.partner_email || '');
          setNote(agreement.factoring_invoice.note || '');
        } else {
          setPartnerName(r.data?.default_partner_name || '');
          setPartnerEmail(r.data?.default_partner_email || '');
        }
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [apiBaseUrl, agreement.factoring_invoice]);

  const submit = async () => {
    setError('');
    if (!partnerName.trim() || !partnerEmail.trim()) {
      setError('Partner name and email are required.');
      return;
    }
    setLoading(true);
    try {
      const r = await api.post(
        `/admin/apprenticeship/agreements/${agreement.id}/factoring-invoice`,
        {
          partner_name: partnerName.trim(),
          partner_email: partnerEmail.trim(),
          note: note.trim() || null,
        }
      );
      setResult(r.data.invoice);
      if (onSaved) onSaved(r.data.invoice);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create Stripe invoice.');
    } finally {
      setLoading(false);
    }
  };

  const isRegen = !!result;

  return (
    <div
      data-testid="factoring-invoice-modal"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white', borderRadius: 14, width: '100%', maxWidth: 620,
          boxShadow: '0 20px 50px rgba(15,23,42,0.3)', overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ background: '#0b2545', color: 'white', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 1, opacity: 0.85 }}>
              Tuition Receivable Factoring
            </div>
            <div style={{ fontSize: '1.05rem', fontWeight: 700, marginTop: 2 }}>
              {isRegen ? 'Regenerate Draft Invoice' : 'Generate $2,500 Draft Invoice'}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="factoring-modal-close"
            style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', padding: 4 }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 20 }}>
          <div style={{ fontSize: '0.85rem', color: '#334155', marginBottom: 14, lineHeight: 1.55 }}>
            Creates a <strong>DRAFT</strong> invoice in Stripe for the $
            {Number(agreement.stated_tuition || 0).toLocaleString()}.00 tuition
            receivable signed by <strong>{agreement.full_name}</strong>
            {' '}on <strong>{new Date(agreement.signed_at).toLocaleDateString()}</strong>.
            The invoice is linked by metadata to ETA-671 agreement
            <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4, marginLeft: 4, fontSize: '0.78rem' }}>{agreement.id}</code>.
            Review / finalize / send from the Stripe dashboard.
          </div>

          {!stripeConfigured && (
            <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: 10, fontSize: '0.8rem', color: '#92400e', marginBottom: 12, display: 'flex', gap: 6 }}>
              <AlertTriangle size={14} />
              Stripe is not configured on the backend. Set <code>STRIPE_API_KEY</code> in backend/.env.
            </div>
          )}
          {stripeMode === 'test' && (
            <div style={{ background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: 8, padding: 8, fontSize: '0.75rem', color: '#1e40af', marginBottom: 12 }}>
              Stripe <strong>TEST</strong> mode — invoice will be created in your test dashboard.
            </div>
          )}

          <Field label="Factoring Partner Name">
            <input
              value={partnerName}
              onChange={(e) => setPartnerName(e.target.value)}
              data-testid="factoring-partner-name-input"
              placeholder="Factoring Partner LLC"
              style={inputStyle}
            />
          </Field>
          <Field label="Factoring Partner Email">
            <input
              type="email"
              value={partnerEmail}
              onChange={(e) => setPartnerEmail(e.target.value)}
              data-testid="factoring-partner-email-input"
              placeholder="factoring@partner.com"
              style={inputStyle}
            />
          </Field>
          <Field label="Admin Memo (optional)">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="factoring-note-input"
              rows={2}
              placeholder="e.g., Batch #2025-11 — assigned to Partner XYZ"
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </Field>

          {error && (
            <div data-testid="factoring-modal-error" style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', padding: 10, borderRadius: 8, fontSize: '0.82rem', marginTop: 6 }}>
              {error}
            </div>
          )}

          {result && (
            <div data-testid="factoring-invoice-result" style={{ marginTop: 14, padding: 14, background: '#ecfdf5', border: '1px solid #86efac', borderRadius: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#047857', fontWeight: 700, marginBottom: 8 }}>
                <CheckCircle2 size={16} />
                Draft invoice saved on file
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8, fontSize: '0.82rem', color: '#064e3b' }}>
                <ResultItem label="Invoice ID" value={result.invoice_id} mono />
                <ResultItem label="Status" value={(result.status || '').toUpperCase()} />
                <ResultItem label="Amount" value={`$${Number(result.amount).toLocaleString()}.00 ${(result.currency || 'usd').toUpperCase()}`} />
                <ResultItem label="Customer" value={result.customer_id} mono />
              </div>
              <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {result.dashboard_url && (
                  <a
                    href={result.dashboard_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="factoring-stripe-dashboard-link"
                    style={linkBtn('#635bff')}
                  >
                    <ExternalLink size={12} /> Open in Stripe Dashboard
                  </a>
                )}
                {result.hosted_invoice_url && (
                  <a
                    href={result.hosted_invoice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="factoring-hosted-invoice-link"
                    style={linkBtn('#0b2545')}
                  >
                    <FileText size={12} /> Hosted Invoice Page
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ borderTop: '1px solid #e2e8f0', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', background: '#f8fafc' }}>
          <button
            onClick={onClose}
            data-testid="factoring-modal-cancel"
            style={{ background: 'transparent', border: '1px solid #cbd5e1', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', color: '#475569' }}
          >
            Close
          </button>
          <button
            onClick={submit}
            disabled={loading || !stripeConfigured}
            data-testid="factoring-create-invoice-btn"
            style={{
              background: loading ? '#94a3b8' : '#0b2545', color: 'white',
              border: 'none', padding: '8px 18px', borderRadius: 6,
              cursor: loading ? 'not-allowed' : 'pointer', fontSize: '0.86rem',
              fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            {loading && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
            {isRegen ? 'Regenerate Draft Invoice' : 'Create Draft Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.6, color: '#64748b', fontWeight: 700, marginBottom: 4 }}>
      {label}
    </div>
    {children}
  </div>
);

const ResultItem = ({ label, value, mono }) => (
  <div>
    <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.5, color: '#047857', opacity: 0.85 }}>{label}</div>
    <div style={{ fontWeight: 600, marginTop: 2, wordBreak: 'break-all', fontFamily: mono ? 'ui-monospace, SFMono-Regular, monospace' : 'inherit', fontSize: mono ? '0.78rem' : '0.88rem' }}>{value || '—'}</div>
  </div>
);

const inputStyle = {
  width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1',
  borderRadius: 8, fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box',
};

const linkBtn = (bg) => ({
  background: bg, color: 'white', textDecoration: 'none',
  padding: '6px 12px', borderRadius: 6, fontSize: '0.78rem',
  fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5,
});

export default FactoringInvoiceModal;
